import { HttpContextToken, HttpRequest, HttpInterceptorFn, HttpHandlerFn } from "@angular/common/http";
import { inject, PLATFORM_ID } from "@angular/core";
import { catchError, finalize, Observable, shareReplay, switchMap, tap, throwError } from "rxjs";
import { Router } from "@angular/router";
import { isPlatformBrowser, Location } from '@angular/common';
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { LoginModalComponent } from "../components/login-modal/login-modal.component";
import { authModuleBaseRoute, authModuleRoutes } from "../routes/api-authorization-routes";
import { AlertService } from "./alert.service";
import { AuthorizeService } from "./authorize.service";
import { StorageService } from "./storage.service";

// Marks a request that has already been retried once after a token refresh —
// a second 401 on the same request means the session is genuinely broken.
export const AUTH_RETRIED = new HttpContextToken<boolean>(() => false);

const REFRESH_ENDPOINT = 'users/getAccessTokenSilently';

// Single-flight refresh shared by every concurrent 401: the first failure
// starts the refresh, the rest wait on the same observable. Module-scoped —
// the browser app is a singleton (the interceptor no-ops during SSR).
let refreshTokens$: Observable<any> | null = null;

function refreshAccessToken(
  authorizeService: AuthorizeService,
  storageService: StorageService
): Observable<any> {
  if (!refreshTokens$) {
    const refreshToken = storageService.getItem("refreshToken");
    refreshTokens$ = authorizeService.getNewAccessTokenSilently(refreshToken!).pipe(
      tap((tokenResponse: any) => {
        storageService.setItem("accessToken", tokenResponse.token);
        storageService.setItem("accessTokenExpirey", tokenResponse.expires);
      }),
      shareReplay(1),
      finalize(() => { refreshTokens$ = null; })
    );
  }
  return refreshTokens$;
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const alertService = inject(AlertService);
  const authorizeService = inject(AuthorizeService);
  const location = inject(Location);
  const modalService = inject(NgbModal);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);

  // SSR: there is no user to dismiss snackbars/modals, so the recovery UX
  // below would return observables that never emit, leaving the request
  // pending forever and hanging the server render (the app never becomes
  // stable). Let errors propagate; service-level catchError handles them.
  if (!isBrowser) {
    return next(req);
  }

  return next(req).pipe(
    catchError(error => {

      // 403: authenticated but not allowed — the API uses ForbiddenException
      // for permission/ownership denials. No token work to do.
      if (error.status === 403) {
        let snackBar = alertService.showSnackBar("You do not have permission to access the requested resource. Dismiss to go back.");
        return snackBar.afterDismissed().pipe(
          switchMap(() => {
            location.back();
            return throwError(() => new Error(error));
          })
        );
      }

      // 401: authentication failed — the token on this request was missing,
      // expired, or invalid. Try to recover the session silently.
      if (error.status === 401) {
        let refreshTokenExpirey = new Date(`${storageService.getItem("refreshTokenExpirey")}`);
        let refreshTokenExpired = (refreshTokenExpirey < new Date());

        // Unrecoverable: the refresh call itself was rejected, this request
        // already got one post-refresh retry, or the refresh token is past
        // its expiry — prompt to log in again.
        if (req.url.includes(REFRESH_ENDPOINT) || req.context.get(AUTH_RETRIED) || refreshTokenExpired) {
          return promptLogin(error, req, next, modalService, storageService, router);
        }

        // Another request's 401 already refreshed the token while this one
        // was in flight — retry with the current token, no second refresh.
        const sentToken = req.headers.get('Authorization')?.replace('Bearer ', '');
        const storedToken = storageService.getItem("accessToken");
        const accessTokenExpirey = new Date(`${storageService.getItem("accessTokenExpirey")}`);
        if (storedToken && sentToken !== storedToken && accessTokenExpirey > new Date()) {
          return processRequestWithToken(storedToken, req, next);
        }

        // Refresh once (shared across concurrent 401s) and retry.
        return refreshAccessToken(authorizeService, storageService).pipe(
          switchMap((tokenResponse: any) => processRequestWithToken(tokenResponse.token, req, next)),
          catchError(() => promptLogin(error, req, next, modalService, storageService, router))
        );
      }

      // Anything else: surface a generic error.
      let snackBar = alertService.showSnackBar("There was an error making your request. Please try again.");
      return snackBar.afterDismissed().pipe(
        switchMap(() => {
          return throwError(() => new Error(error));
        })
      );
    })
  );
};

function promptLogin(
  error: any,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  modalService: NgbModal,
  storageService: StorageService,
  router: Router
) {
  // pass modal instance so it can dismiss itself
  let modalRef = modalService.open(LoginModalComponent);
  modalRef.componentInstance.modalRef = modalRef;
  modalRef.componentInstance.errorText = true;

  // dismiss is fired from clicking away, or manually on login
  return modalRef.dismissed.pipe(
    switchMap(() => {
      if (modalRef.componentInstance.isLoggedIn) {
        let accessToken = storageService.getItem("accessToken");
        return processRequestWithToken(accessToken, req, next);
      } else {
        router.navigate([authModuleBaseRoute, authModuleRoutes.logout]);
        return throwError(() => new Error(error));
      }
    })
  );
}

function processRequestWithToken(token: string | null, req: HttpRequest<any>, next: HttpHandlerFn) {
  if (!!token) {
    req = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
  // One post-recovery retry per request; a repeat 401 goes to the login modal.
  req.context.set(AUTH_RETRIED, true);

  return next(req);
}
